import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { useVoiceStore } from '@/store/voice'
import { P, channelPermissions, has } from '@/lib/permissions'
import { coarsePointer } from '@/lib/mobile'
import { cx } from '@/lib/cx'
import { streamLabel } from '@/lib/streamView'
import type { AvatarDecoration, Member } from '@/types/api'
import { LiveBadge, StreamPlaceholder } from './StreamPreview'
import { TrackVideo } from './TrackVideo'
import styles from './StreamCard.module.css'
import { publicUser } from '@/lib/auth'

const OPEN_DELAY = 300
// успеть довести курсор со строки до карточки
const CLOSE_DELAY = 150
const CARD_WIDTH = 280

interface Props {
  guildId: string
  channelId: string
  userId: string
  name: string
  avatar?: string | undefined
  decoration?: AvatarDecoration | null | undefined
  color: string
}

interface Open {
  rect: DOMRect
  /** Открыта нажатием: остаётся, пока не нажмут мимо или Esc. */
  pinned: boolean
  /** Открыта с клавиатуры — фокус сразу на «Смотреть стрим». */
  focus: boolean
}

/**
 * «В ЭФИРЕ» у показывающего экран в списке каналов. Наведение на всю его строку открывает
 * карточку, как в Discord: «Сейчас стримит», превью и «Смотреть стрим». Нажатие на сам значок
 * открывает её же — так до карточки доходят с телефона и с клавиатуры.
 *
 * Строку значок находит сам (ближайший [data-user-id]): список участников под каналом
 * не должен знать про карточку, чтобы добавить её одной строкой.
 */
export function LiveStream({ guildId, channelId, userId, name, avatar, decoration, color }: Props) {
  const badgeRef = useRef<HTMLButtonElement | null>(null)
  const timers = useRef<{ open?: ReturnType<typeof setTimeout>; close?: ReturnType<typeof setTimeout> }>({})
  const [open, setOpen] = useState<Open | null>(null)
  // список каналов перерисовывается на каждое «говорит» — обработчики карточки не должны меняться с ним
  const close = useCallback(() => setOpen(null), [])
  const keep = useCallback(() => clearTimeout(timers.current.close), [])
  const release = useCallback(() => {
    timers.current.close = setTimeout(() => setOpen((o) => (o?.pinned ? o : null)), CLOSE_DELAY)
  }, [])

  useEffect(() => {
    const row = badgeRef.current?.closest<HTMLElement>('[data-user-id]')
    // пальцем наведения нет: карточку открывает нажатие на значок
    if (!row || coarsePointer) return
    const t = timers.current
    const enter = () => {
      clearTimeout(t.close)
      t.open = setTimeout(() => setOpen((o) => o ?? { rect: row.getBoundingClientRect(), pinned: false, focus: false }), OPEN_DELAY)
    }
    const leave = () => {
      clearTimeout(t.open)
      t.close = setTimeout(() => setOpen((o) => (o?.pinned ? o : null)), CLOSE_DELAY)
    }
    row.addEventListener('mouseenter', enter)
    row.addEventListener('mouseleave', leave)
    return () => {
      clearTimeout(t.open)
      clearTimeout(t.close)
      row.removeEventListener('mouseenter', enter)
      row.removeEventListener('mouseleave', leave)
    }
  }, [])

  const onBadge = (e: ReactMouseEvent<HTMLButtonElement>) => {
    // строка по нажатию открывает карточку профиля — здесь нужна карточка трансляции
    e.stopPropagation()
    const row = e.currentTarget.closest<HTMLElement>('[data-user-id]') ?? e.currentTarget
    const rect = row.getBoundingClientRect()
    // detail 0 — нажатие с клавиатуры (Enter, пробел), а не мышью
    const focus = e.detail === 0
    setOpen((o) => (o?.pinned ? null : { rect, pinned: true, focus }))
  }

  return (
    <>
      <button
        ref={badgeRef}
        type="button"
        className={styles.badge}
        aria-label={`${name} в эфире: открыть трансляцию`}
        aria-haspopup="dialog"
        aria-expanded={open !== null}
        onClick={onBadge}
      >
        <LiveBadge />
      </button>
      {open ? (
        <StreamCard
          guildId={guildId}
          channelId={channelId}
          userId={userId}
          name={name}
          avatar={avatar}
          decoration={decoration}
          color={color}
          open={open}
          anchor={badgeRef}
          onKeep={keep}
          onRelease={release}
          onClose={close}
        />
      ) : null}
    </>
  )
}

interface CardProps extends Props {
  open: Open
  /** Значок: нажатие на него не считается нажатием мимо — он сам закрывает и открывает карточку. */
  anchor: RefObject<HTMLElement | null>
  onKeep: () => void
  onRelease: () => void
  onClose: () => void
}

function StreamCard({ guildId, channelId, userId, name, avatar, decoration, color, open, anchor, onKeep, onRelease, onClose }: CardProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const watchRef = useRef<HTMLButtonElement | null>(null)
  const me = useAuthStore((s) => s.user)
  const guild = useGuildStore((s) => s.guilds[guildId])
  const channel = useGuildStore((s) => s.channels[guildId]?.[channelId])
  const member = useGuildStore((s) => (me ? s.members[guildId]?.[me.id] : undefined))
  const inRoom = useVoiceStore((s) => s.connection?.channelId === channelId)
  // кадр есть, только если дорожка уже здесь: свой экран или трансляция, которую смотрим
  const track = useVoiceStore((s) => (s.connection?.channelId === channelId && (userId === me?.id || s.watching[userId]) ? s.screens[userId] : undefined))
  const title = useVoiceStore((s) => (s.connection?.channelId === channelId ? s.live[userId] : undefined)) ?? ''
  const self: Member | undefined = member ?? (me ? { user: publicUser(me), nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null } : undefined)
  const canConnect = guild && self ? has(channelPermissions(guild, self, channel), P.CONNECT) : false
  const blocked = !inRoom && !canConnect

  // справа от строки, а если места нет (узкое окно, телефон) — под ней; высоту знаем после отрисовки
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const { rect } = open
    let left = rect.right + 8
    let top = rect.top - 8
    if (left + CARD_WIDTH > window.innerWidth - 8) {
      left = Math.max(8, Math.min(rect.left, window.innerWidth - CARD_WIDTH - 8))
      top = rect.bottom + 4
    }
    el.style.left = `${left}px`
    el.style.top = `${Math.max(8, Math.min(top, window.innerHeight - el.offsetHeight - 8))}px`
  }, [open])

  useEffect(() => {
    if (open.focus) watchRef.current?.focus()
  }, [open.focus])

  useEffect(() => {
    const outside = (target: EventTarget | null) => target instanceof Node && !ref.current?.contains(target) && !anchor.current?.contains(target)
    const onDown = (e: MouseEvent) => {
      if (outside(e.target)) onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      onClose()
      if (open.pinned) anchor.current?.focus()
    }
    // строка уехала при прокрутке списка — карточка висела бы у пустого места
    const onScroll = (e: Event) => {
      if (outside(e.target)) onClose()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    document.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onClose)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onClose)
    }
  }, [anchor, onClose, open.pinned])

  const watch = async () => {
    onClose()
    useAppStore.getState().setView({ kind: 'guild', guildId, channelId })
    const voice = useVoiceStore.getState()
    if (voice.connection?.channelId !== channelId) {
      try {
        await voice.join(guildId, channelId)
      } catch {
        return // почему не вышло, скажет сама комната канала
      }
    }
    const after = useVoiceStore.getState()
    // трансляция могла закончиться, пока подключались, — тогда просто остаёмся в канале
    if (after.connection?.channelId === channelId && userId in after.live) after.watchStream(userId)
  }

  // события React идут сквозь портал к предкам по дереву компонентов: без этого нажатие
  // в карточке доходило до строки участника и открывало ещё и его профиль
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()

  return createPortal(
    <div
      ref={ref}
      className={cx(styles.card, 'klichat-popover')}
      role="dialog"
      aria-label={`Трансляция: ${name}`}
      style={{ width: CARD_WIDTH, '--popover-origin': 'top left' } as CSSProperties}
      onMouseEnter={onKeep}
      onMouseLeave={open.pinned ? undefined : onRelease}
      onClick={stop}
      onMouseDown={stop}
      onPointerDown={stop}
      onContextMenu={stop}
    >
      <div className={styles.head}>
        <span className={styles.kicker}>Сейчас стримит</span>
        <LiveBadge />
      </div>
      <div className={styles.preview}>
        {track ? (
          <>
            <TrackVideo track={track} className={styles.video} />
            <span className={styles.previewName}>{streamLabel(name)}</span>
          </>
        ) : (
          <StreamPlaceholder name={name} title={title} avatar={avatar} decoration={decoration} color={color} size={48} />
        )}
      </div>
      <button ref={watchRef} type="button" className={styles.watch} disabled={blocked} onClick={() => void watch()}>
        Смотреть стрим
      </button>
      {blocked ? <p className={styles.note}>Нет права подключаться к этому каналу</p> : null}
    </div>,
    document.body,
  )
}
